'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

import { VCARD_HERO_LANDING_PREVIEW } from '@/constants/utils'
import { VCardPreviewType } from '@/lib/types/VCardT'
import { cn } from '@/lib/utils'
import { EASE_OUT } from '@/components/livecontact/motion'

import { PhoneSlide, ProfileSlide, QrSlide, SignatureSlide } from './HeroAssetSlides'

/** Dwell per asset. Long for UI, but this is explanatory motion — people need to read the card. */
const DWELL_MS = 5200
/** Widest asset on the stage (the signature); below lg the stage is scaled so it just fits. */
const STAGE_WIDTH = 420

type Props = {
  data?: VCardPreviewType | null
  previewSlug?: string
  className?: string
}

type Slide = {
  key: string
  label: string
  render: (props: { data: VCardPreviewType | null; previewSlug?: string }) => React.ReactNode
}

const SLIDES: Slide[] = [
  { key: 'profile', label: 'Profile page', render: (p) => <ProfileSlide {...p} /> },
  { key: 'ios', label: 'iOS contact card', render: (p) => <PhoneSlide {...p} /> },
  { key: 'qr', label: 'Branded QR code', render: (p) => <QrSlide {...p} /> },
  { key: 'signature', label: 'Email signature', render: (p) => <SignatureSlide {...p} /> },
]

/**
 * Hero showcase: one asset at a time on the tinted panel, cycling on its own.
 *
 * The crossfade carries a 4px blur so the eye reads one object changing rather than two cards
 * overlapping, and the outgoing slide leaves faster than the incoming one arrives. Autoplay
 * pauses on hover, on keyboard focus and while the tab is hidden — the progress pill and the
 * timer are driven by the same rAF clock, so the bar always tells the truth about when the
 * next asset lands. Reduced motion keeps the rotation but drops the movement and the sweep.
 */
const HeroAssetCarousel = ({
  data = VCARD_HERO_LANDING_PREVIEW,
  previewSlug,
  className,
}: Props) => {
  const reduced = useReducedMotion()
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const barRef = useRef<HTMLSpanElement | null>(null)
  const elapsedRef = useRef(0)

  const slide = SLIDES[index]
  const slideProps = useMemo(() => ({ data, previewSlug }), [data, previewSlug])

  // Below lg the height-based scales don't apply and the panel is a fixed height on a narrow
  // screen, so the stage keeps its designed width and is scaled, whole, to fit the panel —
  // the way a picture shrinks — instead of the panel clipping the asset.
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [fit, setFit] = useState(1)
  useEffect(() => {
    const root = rootRef.current
    const panel = root?.parentElement
    if (!root || !panel) return undefined
    const query = window.matchMedia('(max-width: 1023px)')

    const measure = () => {
      if (!query.matches) return setFit(1)
      const s = getComputedStyle(panel)
      const width = panel.clientWidth - parseFloat(s.paddingLeft) - parseFloat(s.paddingRight)
      const height = panel.clientHeight - parseFloat(s.paddingTop) - parseFloat(s.paddingBottom)
      // offsetHeight ignores transforms; the extra 32px covers the stage's lift.
      setFit(Math.min(1, width / STAGE_WIDTH, height / (root.offsetHeight + 32)))
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(panel)
    query.addEventListener('change', measure)
    return () => {
      observer.disconnect()
      query.removeEventListener('change', measure)
    }
  }, [])

  const goTo = useCallback((next: number) => setIndex(next % SLIDES.length), [])

  // Pause while the tab is in the background — an unseen carousel shouldn't burn through
  // its slides, so people come back to where they left off.
  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden)
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  // One clock for both the advance and the progress pill, so they can never drift apart.
  useEffect(() => {
    if (paused) return undefined

    let frame = 0
    let last = performance.now()
    let elapsed = elapsedRef.current

    const tick = (now: number) => {
      elapsed += now - last
      last = now
      elapsedRef.current = elapsed

      if (barRef.current && !reduced) {
        barRef.current.style.transform = `scaleX(${Math.min(1, elapsed / DWELL_MS)})`
      }

      if (elapsed >= DWELL_MS) {
        elapsedRef.current = 0
        setIndex((i) => (i + 1) % SLIDES.length)
        return
      }
      frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [index, paused, reduced])

  // A manual jump restarts the dwell rather than inheriting the leftover of the last slide.
  const handleJump = useCallback(
    (next: number) => {
      elapsedRef.current = 0
      if (barRef.current) barRef.current.style.transform = 'scaleX(0)'
      goTo(next)
    },
    [goTo]
  )

  return (
    <div
      ref={rootRef}
      style={fit < 1 ? { scale: String(fit) } : undefined}
      className={cn('flex flex-col items-center gap-5', className)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {/* Stage — every slide is absolutely placed so the panel never reflows between assets. Tall
          enough for the profile card, the longest of the four, so nothing has to be clipped. */}
      <div className="relative flex h-[744px] w-full items-center justify-center">
        <AnimatePresence initial={false}>
          <motion.div
            key={slide.key}
            className="absolute inset-0 flex items-center justify-center"
            initial={
              reduced
                ? { opacity: 0 }
                : { opacity: 0, transform: 'translateY(14px) scale(0.97)', filter: 'blur(4px)' }
            }
            animate={
              reduced
                ? { opacity: 1 }
                : { opacity: 1, transform: 'translateY(0px) scale(1)', filter: 'blur(0px)' }
            }
            exit={
              reduced
                ? { opacity: 0, transition: { duration: 0.18 } }
                : {
                    opacity: 0,
                    transform: 'translateY(-12px) scale(0.98)',
                    filter: 'blur(4px)',
                    transition: { duration: 0.26, ease: EASE_OUT },
                  }
            }
            transition={{ duration: 0.44, ease: EASE_OUT }}
          >
            {slide.render(slideProps)}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Caption + progress. This row is the only thing telling people the panel is a reel rather
          than a static screenshot, so it reads at body weight and the running slide fills in
          brand blue — a hue change, not just a value change, so the active one is unmistakable. */}
      <div className="flex w-full items-center justify-between gap-4">
        <p
          aria-live="polite"
          className="text-[16px] font-semibold leading-[1.4] tracking-[-0.2px] text-ink"
        >
          {slide.label}
        </p>

        <div className="flex items-center gap-1.5">
          {SLIDES.map((item, i) => {
            const active = i === index
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleJump(i)}
                aria-label={`Show ${item.label}`}
                aria-current={active}
                className={cn(
                  'group/dot relative flex h-6 items-center justify-center rounded-full outline-none',
                  'transition-[width] duration-[280ms] ease-out-strong',
                  active ? 'w-14' : 'w-2.5'
                )}
              >
                <span
                  className={cn(
                    'block h-2.5 w-full overflow-hidden rounded-full',
                    'transition-colors duration-[160ms] ease-out',
                    // The active track is already brand-tinted, so the running slide reads as
                    // active the instant it changes — not only once the fill has swept across.
                    active ? 'bg-brand/25' : 'bg-ink/15 group-hover/dot:bg-ink/30',
                    'group-focus-visible/dot:ring-2 group-focus-visible/dot:ring-brand/40'
                  )}
                >
                  {active && (
                    <span
                      ref={barRef}
                      aria-hidden
                      className="block h-full w-full origin-left rounded-full bg-brand"
                      style={{ transform: reduced ? 'scaleX(1)' : 'scaleX(0)' }}
                    />
                  )}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default HeroAssetCarousel
